import React, { useEffect, useMemo, useRef, useState } from "react";
import { Col, Container, Dropdown, Form, Modal, Row } from "react-bootstrap";
import styled from "styled-components";
import CurrencyFormat from "react-currency-format";
import { BiEdit, BiErrorCircle, BiFilterAlt } from "react-icons/bi";
import { FiFileText, FiUpload, FiX } from "react-icons/fi";
import { Checkbox, Table, Whisper, Popover } from "rsuite";
import {
  getBillingsAdmin,
  getUserPending,
  patchProviderProformaInvoice,
} from "../../../../../api/modules";
import SearchToInvoice from "./Components/SearchToInvoice";
import ModalAuditedWeight from "./Components/ModalAuditedWeight";
import LogButton from "../../../../../components/atoms/LogButton";
import ModalInvoice from "./Components/ModalInvoice";
import ModalReceipt from "./Components/Modalreceipt";
import ModalLp from "./Components/ModalLp";
import ModalSalesVatBook from "./Components/ModalSalesVatBook";
import ModalPurchaseVatBook from "./Components/ModalPurchaseVatBook";
import { CardLogBootstrapStyled } from "../../../../../styledComponents/CardBootstrapLog";
import { useSelector } from "react-redux";
import ModalError from "./Components/ModalError";
import PendingInvoiceRetry from "./Components/PendingInvoiceRetry";
import {
  errorToast,
  successToast,
} from "../../../../../services/toastService";
import LogInput from "../../../../../components/atoms/LogInput";
import { COLOR_GRAY_TEXT } from "../../../../../constants/Colors";
const { Column, HeaderCell, Cell, ColumnGroup } = Table;
const TAXES_LABEL = process.env.REACT_APP_TAXES_LABEL;

const ModalLogStyled = styled(Modal)`
  opacity: 1;

  .modal-content {
    border-radius: 15px !important;
    padding: 20px;
    border: none;
  }

  .modal-header,
  .modal-body,
  .modal-footer {
    border: none;
    padding-left: 0;
    padding-right: 0;
  }

  .modal-header {
    padding-top: 0;
    padding-bottom: 0;
  }

  .modal-body {
    padding-bottom: 0;
  }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 10px;
  right: 10px;
  background-color: transparent;
  border: none;
  cursor: pointer;
  font-size: 20px;
  color: #000;

  &:hover {
    color: #ff0000;
  }
`;

const UploadBox = styled.label`
  width: 100%;
  min-height: 92px;
  border: 1px dashed #cfd5d9;
  border-radius: 8px;
  background-color: #f6f6f4;
  color: ${COLOR_GRAY_TEXT};
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  text-align: center;
  padding: 14px;
  cursor: pointer;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  &:hover {
    border-color: #93c01f;
    background-color: #fbfff4;
  }

  input {
    display: none;
  }

  span {
    color: #343a40;
    font-weight: 600;
    margin-top: 6px;
    word-break: break-word;
  }

  small {
    color: ${COLOR_GRAY_TEXT};
  }
`;

const ExistingProviderFile = styled.small`
  color: #93c01f;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 5px;
  margin-top: 8px;
`;

const getActiveCreditNoteAmount = (invoice) =>
  (invoice?.credit_notes || [])
    .filter((creditNote) =>
      !["error", "unauthorized"].includes(creditNote.status)
    )
    .reduce(
      (total, creditNote) =>
        total +
        (Number(creditNote?.amount ?? creditNote?.requested_amount) || 0),
      0
    );

const getTomadorCommissionAmount = (rowData) => {
  if (typeof rowData.comision_tomador_pesos === "number") {
    return rowData.comision_tomador_pesos;
  }

  const base =
    (Number(rowData.auditedWeight) || 0) * (Number(rowData.price_per_ton) || 0);

  if (typeof rowData.discount_trip === "number") {
    return (base * (rowData.discount_trip / 2)) / 100;
  }

  return (base * (rowData.discount_tomador || 0)) / 100;
};

const hasTomadorCommission = (rowData) =>
  getTomadorCommissionAmount(rowData) > 0;

const getPaymentOrderTotal = (paymentOrders = []) =>
  paymentOrders.reduce(
    (total, paymentOrder) => total + (Number(paymentOrder.amount) || 0),
    0
  );

const isCompraventaInvoice = (rowData) =>
  rowData?.invoice_id?.billing_flow_mode === "compraventa";

const getLpLikeDocument = (rowData) =>
  isCompraventaInvoice(rowData) ? rowData.provider_proforma_id : rowData.lp_id;

const DocumentAction = ({ number, onClick }) => (
  <div className="d-flex flex-column align-items-center">
    <BiEdit className="cursor-pointer mb-1" onClick={onClick} />
    <small className="text-primary number-font text-center">{number}</small>
  </div>
);

const DOCUMENT_STATUS_OPTIONS = [
  { value: "status:missing", label: "Sin comprobante", group: "Estado" },
  { value: "status:pending", label: "Pendiente", group: "Estado" },
  { value: "status:error", label: "Error", group: "Estado" },
  { value: "status:authorized", label: "Autorizado", group: "Estado" },
];

const LP_DOCUMENT_STATUS_OPTIONS = [
  { value: "status:lp_missing", label: "Pendiente de LP", group: "Estado" },
  {
    value: "status:provider_proforma_pending",
    label: "Pendiente de proforma",
    group: "Estado",
  },
  { value: "status:pending", label: "Pendiente", group: "Estado" },
  { value: "status:error", label: "Error", group: "Estado" },
  { value: "status:authorized", label: "Autorizado", group: "Estado" },
];

const INITIAL_DOCUMENT_FILTERS = {
  invoice: [],
  lp: [],
  commission: [],
};

const normalizeSearchText = (value) =>
  (value || "").toString().trim().toLowerCase();

const getDocumentNumber = (document, keys = ["number"]) =>
  keys.map((key) => document?.[key]).find(Boolean) || "";

const formatDateInputValue = (value) => {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
};

const getDocumentStatus = (document, keys = ["number"]) => {
  if (!document?._id) return "missing";
  if (["error", "unauthorized"].includes(document.status)) return "error";
  if (
    ["authorized", "generated"].includes(document.status) &&
    getDocumentNumber(document, keys)
  ) {
    return "authorized";
  }
  return "pending";
};

const documentMatchesFilter = (
  document,
  selectedFilters,
  keys = ["number"],
  context = {}
) => {
  if (!selectedFilters?.length) return true;

  const status = getDocumentStatus(document, keys);
  const number = normalizeSearchText(getDocumentNumber(document, keys));
  const selectedStatuses = selectedFilters
    .filter((value) => value.startsWith("status:"))
    .map((value) => value.replace("status:", ""));
  const selectedNumbers = selectedFilters
    .filter((value) => value.startsWith("number:"))
    .map((value) => normalizeSearchText(value.replace("number:", "")));

  if (selectedStatuses.length) {
    const providerInvoiceReceived = Boolean(document?.provider_invoice_pdf_url);
    const statusMatches = selectedStatuses.some((selectedStatus) => {
      if (selectedStatus === "lp_missing") {
        return (
          context.column === "lp" &&
          !isCompraventaInvoice(context.row) &&
          !context.row?.lp_id?._id &&
          context.row?.invoice_id?.status === "authorized"
        );
      }
      if (selectedStatus === "provider_proforma_pending") {
        return (
          context.column === "lp" &&
          isCompraventaInvoice(context.row) &&
          !context.row?.provider_proforma_id?._id &&
          context.row?.invoice_id?.status === "authorized"
        );
      }
      if (selectedStatus === "provider_invoice_pending") {
        return (
          context.column === "lp" &&
          isCompraventaInvoice(context.row) &&
          document?._id &&
          document.number &&
          !providerInvoiceReceived
        );
      }
      if (selectedStatus === "provider_invoice_received") {
        return (
          context.column === "lp" &&
          isCompraventaInvoice(context.row) &&
          providerInvoiceReceived
        );
      }
      return selectedStatus === status;
    });

    if (!statusMatches) return false;
  }
  if (selectedNumbers.length && !selectedNumbers.includes(number)) {
    return false;
  }

  return true;
};

const buildDocumentFilterOptions = (
  rows = [],
  getDocument,
  keys = ["number"],
  extraOptions = [],
  baseOptions = DOCUMENT_STATUS_OPTIONS
) => {
  const numbers = [
    ...new Set(
      rows
        .map((row) => getDocumentNumber(getDocument(row), keys))
        .filter(Boolean)
    ),
  ].sort();

  return [
    ...baseOptions,
    ...extraOptions,
    ...numbers.map((number) => ({
      value: `number:${number}`,
      label: number,
      group: "Comprobantes",
    })),
  ];
};

const applyVisibleRowSpan = (rows = []) => {
  const nextRows = rows.map((row) => ({
    ...row,
    row_span: 1,
    lp_row_span: 1,
    commission_row_span: 1,
    invoice_group_start: false,
    invoice_group_inner: false,
    invoice_group_open: false,
    invoice_group_end: false,
  }));

  let index = 0;
  while (index < nextRows.length) {
    const invoiceId = nextRows[index].invoice_id?._id?.toString();

    if (!invoiceId) {
      nextRows[index].invoice_group_start = true;
      nextRows[index].invoice_group_end = true;
      index++;
      continue;
    }

    let count = 1;
    while (
      index + count < nextRows.length &&
      nextRows[index + count].invoice_id?._id?.toString() === invoiceId
    ) {
      count++;
    }

    nextRows[index].row_span = count;
    nextRows[index].invoice_group_start = true;
    nextRows[index].invoice_group_open = count > 1;
    nextRows[index].invoice_group_end = count === 1;

    for (let offset = 1; offset < count; offset++) {
      nextRows[index + offset].row_span = 0;
      nextRows[index + offset].invoice_group_inner = offset < count - 1;
      nextRows[index + offset].invoice_group_end = offset === count - 1;
    }

    index += count;
  }

  const applyDocumentSpan = (getGroupKey, spanKey) => {
    let spanIndex = 0;

    while (spanIndex < nextRows.length) {
      const groupKey = getGroupKey(nextRows[spanIndex]);

      if (!groupKey) {
        nextRows[spanIndex][spanKey] = 1;
        spanIndex++;
        continue;
      }

      let count = 1;
      while (
        spanIndex + count < nextRows.length &&
        getGroupKey(nextRows[spanIndex + count]) === groupKey
      ) {
        count++;
      }

      nextRows[spanIndex][spanKey] = count;
      for (let offset = 1; offset < count; offset++) {
        nextRows[spanIndex + offset][spanKey] = 0;
      }

      spanIndex += count;
    }
  };

  applyDocumentSpan((row) => {
    if (isCompraventaInvoice(row)) {
      if (row.provider_proforma_id?._id) {
        return `proforma:${row.provider_proforma_id._id}`;
      }
      if (row.invoice_id?._id && row.id_tomador?._id) {
        return `proforma-pending:${row.invoice_id._id}:${row.id_tomador._id}`;
      }
      return null;
    }

    if (row.lp_id?._id) return `lp:${row.lp_id._id}`;
    if (row.invoice_id?._id && row.id_tomador?._id) {
      return `lp-pending:${row.invoice_id._id}:${row.id_tomador._id}`;
    }
    return null;
  }, "lp_row_span");

  applyDocumentSpan((row) => {
    if (row.invoice_tomador_comision?._id) {
      return `tcom:${row.invoice_tomador_comision._id}`;
    }
    if (row.lp_id?._id) return `tcom-pending:${row.lp_id._id}`;
    if (row.invoice_id?._id && row.id_tomador?._id) {
      return `tcom-no-lp:${row.invoice_id._id}:${row.id_tomador._id}`;
    }
    return null;
  }, "commission_row_span");

  return nextRows;
};

const DocumentFilterHeader = ({ title, data, value, onChange }) => {
  const isActive = value?.length > 0;
  const [search, setSearch] = useState("");
  const selectedValues = new Set(value || []);
  const filteredOptions = data.filter((option) =>
    normalizeSearchText(option.label).includes(normalizeSearchText(search))
  );
  const groupedOptions = filteredOptions.reduce((groups, option) => {
    const group = option.group || "Otros";
    return {
      ...groups,
      [group]: [...(groups[group] || []), option],
    };
  }, {});

  const handleToggle = (optionValue, checked) => {
    const nextValue = checked
      ? Array.from(new Set([...(value || []), optionValue]))
      : (value || []).filter((item) => item !== optionValue);

    onChange(nextValue);
  };

  const speaker = (
    <Popover>
      <div style={{ width: 280 }}>
        <input
          type="text"
          className="form-control form-control-sm mb-2"
          value={search}
          onChange={(event) => setSearch(event.target.value)}
          placeholder="Buscar"
        />
        <div style={{ maxHeight: 240, overflowY: "auto" }}>
          {Object.entries(groupedOptions).map(([group, options]) => (
            <div key={group} className="mb-2">
              <small className="d-block text-muted font-weight-bold mb-1">
                {group}
              </small>
              {options.map((option) => (
                <Checkbox
                  key={option.value}
                  className="d-block"
                  value={option.value}
                  checked={selectedValues.has(option.value)}
                  onChange={handleToggle}
                >
                  {option.label}
                </Checkbox>
              ))}
            </div>
          ))}
          {!filteredOptions.length && (
            <small className="text-muted">Sin resultados</small>
          )}
        </div>
        {isActive && (
          <button
            type="button"
            className="btn btn-link btn-sm p-0 mt-2 text-primary"
            onClick={() => onChange([])}
          >
            Limpiar
          </button>
        )}
      </div>
    </Popover>
  );

  return (
    <div className="d-flex align-items-center justify-content-center">
      <span>{title}</span>
      <Whisper placement="bottomEnd" trigger="click" speaker={speaker}>
        <button
          type="button"
          className="btn btn-link p-0 ml-1"
          style={{ color: isActive ? "#343a40" : "#fff", lineHeight: 1 }}
        >
          <BiFilterAlt size={17} />
        </button>
      </Whisper>
    </div>
  );
};

function BillingAdmin() {
  const [showAudited, setShowAudited] = useState({ isShow: false, data: {} });
  const [invoiceModal, setInvoiceModal] = useState({ isShow: false, data: {} });
  const [receiptModal, setReceiptModal] = useState({ isShow: false, data: {} });
  const isLoading = useSelector((state) => state.appReducer.isLoading);
  const [lpModal, setLpModal] = useState({
    isShow: false,
    data: {},
    amount: 0,
  });
  const [providerInvoiceModal, setProviderInvoiceModal] = useState({
    isShow: false,
    proforma: null,
    form: {
      provider_invoice_number: "",
      provider_invoice_date: "",
      provider_invoice_amount: "",
      provider_invoice_file: null,
    },
  });
  const INITIAL_STATE = {
    total: 0,
    data: [],
    id_dador: null,
    id_tomador: null,
  };
  let checked = false;
  let indeterminate = false;
  const [dadorBalance, setDadorBalance] = useState("");
  const [dadorInvoice, setDadorInvoice] = useState("");
  const [tomadorBalance, setTomadorBalance] = useState("");
  const [tomadorLp, setTomadorLp] = useState("");
  const [errorModal, setErrorModal] = useState({
    isShow: false,
    type: null,
    data: null,
  });
  const [salesVatBookModal, setSalesVatBookModal] = useState(false);
  const [purchaseVatBookModal, setPurchaseVatBookModal] = useState(false);
  const openErrorModal = ({ type, data }) =>
    setErrorModal({ isShow: true, type, data });
  const closeErrorModal = () =>
    setErrorModal({ isShow: false, type: null, data: null });
  const [refetch, setRefetch] = useState(false);
  const [billing, setBilling] = useState(INITIAL_STATE);
  const [checkeableRows, setCheckeableRows] = useState(INITIAL_STATE);
  const [query, setQuery] = useState({ id_tomador: "", id_dador: "" });
  const [pendingDador, setPendingDador] = useState([]);
  const [pendingTomador, setPendingTomador] = useState([]);
  const [selectedDador, setSelectedDador] = useState("");
  const [checkedKeys, setCheckedKeys] = React.useState([]);
  const [documentFilters, setDocumentFilters] = useState(
    INITIAL_DOCUMENT_FILTERS
  );
  const closeAuditedModal = () => setShowAudited({ isShow: false, data: {} });
  const openAuditedModal = (rowData) =>
    setShowAudited({ isShow: true, data: rowData });
  const closeReceiptModal = () => setReceiptModal({ isShow: false, data: {} });
  const getInvoiceRows = (rowData, rows = billing.data, options = {}) => {
    const invoiceTripIds = new Set(
      (rowData.invoice_id?.trip_details || []).map((tripId) =>
        tripId?.toString()
      )
    );
    const invoiceId = rowData.invoice_id?._id?.toString();
    const tomadorId = options.id_tomador?._id || options.id_tomador;

    return rows.filter((row) => {
      const belongsToInvoice =
        invoiceTripIds.has(row._id?.toString()) ||
        (invoiceId && row.invoice_id?._id?.toString() === invoiceId);
      if (!belongsToInvoice) return false;

      return tomadorId ? row.id_tomador?._id === tomadorId : true;
    });
  };
  const getTomadorInvoiceRows = (rowData, rows = billing.data) =>
    getInvoiceRows(rowData, rows, { id_tomador: rowData.id_tomador });
  const getLpRows = (rowData, rows = billing.data) => {
    const lpTripIds = new Set(
      (rowData.lp_id?.trip_details || []).map((tripId) => tripId?.toString())
    );
    const lpId = rowData.lp_id?._id?.toString();

    if (!lpTripIds.size && !lpId) return getTomadorInvoiceRows(rowData, rows);

    return rows.filter((row) => {
      if (lpTripIds.has(row._id?.toString())) return true;
      return lpId && row.lp_id?._id?.toString() === lpId;
    });
  };
  const buildReceiptModalData = (rowData, rows = billing.data) => ({
    ...rowData,
    invoice_trip_rows: getInvoiceRows(rowData, rows),
  });
  const openReceiptModal = (rowData) =>
    setReceiptModal({ isShow: true, data: buildReceiptModalData(rowData) });
  const closeLpModal = () => setLpModal({ isShow: false, data: {}, amount: 0 });
  const getBillingRowClassName = (rowData) => {
    const classes = ["p-0"];

    if (rowData?.invoice_group_start) {
      classes.push("billing-invoice-group-start");
    }
    if (
      (rowData?.invoice_group_open || rowData?.invoice_group_inner) &&
      !rowData?.invoice_group_end
    ) {
      classes.push("billing-invoice-group-open");
    }

    return classes.join(" ");
  };
  const openLpModal = (rowData) => {
    if (isCompraventaInvoice(rowData) && rowData.provider_proforma_id?.number) {
      setLpModal({ isShow: true, data: rowData, amount: 0 });
      return;
    }

    const filtered = getLpRows(rowData);
    const amount = filtered.reduce((accumulator, rowData) => {
      const discount = rowData.discount_trip
        ? rowData.price_per_ton *
          rowData.auditedWeight *
          (rowData.discount_trip / 100)
        : rowData.price_per_ton *
          rowData.auditedWeight *
          ((rowData.discount_dador + rowData.discount_tomador) / 100);

      return accumulator + parseFloat(discount.toFixed(2));
    }, 0);
    setLpModal({ isShow: true, data: rowData, amount: amount });
  };

  const openTryAgain = (rowData, actionLabel = "Avanzar facturación") => {
    const invoiceTrips =
      actionLabel === "Generar comisión tomador"
        ? getLpRows(rowData)
        : getTomadorInvoiceRows(rowData);

    setInvoiceModal({
      isShow: true,
      data: invoiceTrips,
      tryAgain: true,
      actionLabel,
    });
  };
  const openProviderProformaEditModal = (rowData) => {
    const proformaTripIds = new Set(
      (rowData.provider_proforma_id?.trip_details || []).map((tripId) =>
        tripId?.toString()
      )
    );
    const proformaId = rowData.provider_proforma_id?._id?.toString();
    const proformaRows =
      proformaTripIds.size || proformaId
        ? billing.data.filter((row) => {
            if (proformaTripIds.has(row._id?.toString())) return true;
            return (
              proformaId &&
              row.provider_proforma_id?._id?.toString() === proformaId
            );
          })
        : [rowData];

    setInvoiceModal({
      isShow: true,
      data: proformaRows.length ? proformaRows : [rowData],
      tryAgain: true,
      actionLabel: "Editar proforma",
      providerProforma: rowData.provider_proforma_id,
    });
  };
  const closeProviderInvoiceModal = () =>
    setProviderInvoiceModal({
      isShow: false,
      proforma: null,
      form: {
        provider_invoice_number: "",
        provider_invoice_date: "",
        provider_invoice_amount: "",
        provider_invoice_file: null,
      },
    });
  const openProviderInvoiceModal = (proforma) =>
    setProviderInvoiceModal({
      isShow: true,
      proforma,
      form: {
        provider_invoice_number: proforma?.provider_invoice_number || "",
        provider_invoice_date: formatDateInputValue(
          proforma?.provider_invoice_date
        ),
        provider_invoice_amount: proforma?.provider_invoice_amount || "",
        provider_invoice_file: null,
      },
    });
  const updateProviderInvoiceForm = (field, value) =>
    setProviderInvoiceModal((current) => ({
      ...current,
      form: {
        ...current.form,
        [field]: value,
      },
    }));
  const submitProviderInvoice = async () => {
    const { proforma, form } = providerInvoiceModal;

    if (!proforma?._id) return;

    if (
      !form.provider_invoice_number ||
      !form.provider_invoice_date ||
      !form.provider_invoice_amount
    ) {
      errorToast("Completá número, fecha e importe");
      return;
    }

    if (!form.provider_invoice_file && !proforma.provider_invoice_pdf_url) {
      errorToast("Adjuntá la factura del proveedor");
      return;
    }

    const formData = new FormData();
    formData.append(
      "provider_invoice_number",
      form.provider_invoice_number
    );
    formData.append("provider_invoice_date", form.provider_invoice_date);
    formData.append("provider_invoice_amount", form.provider_invoice_amount);

    if (form.provider_invoice_file) {
      formData.append("provider_invoice_file", form.provider_invoice_file);
    }

    const resp = await patchProviderProformaInvoice(proforma._id, formData);

    if (resp?.msg) {
      errorToast(resp.msg);
      return;
    }

    successToast("Factura proveedor cargada");
    closeProviderInvoiceModal();
    setRefetch((prev) => !prev);
  };

  const updateDocumentFilter = (key, value) => {
    setDocumentFilters((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const resetDocumentFilters = () =>
    setDocumentFilters(INITIAL_DOCUMENT_FILTERS);
  const hasActiveDocumentFilters = Object.values(documentFilters).some(
    (value) => value.length
  );

  const invoiceFilterOptions = useMemo(
    () =>
      buildDocumentFilterOptions(
        billing.data,
        (row) => row.invoice_id,
        ["invoice_number", "number"]
      ),
    [billing.data]
  );
  const lpFilterOptions = useMemo(
    () =>
      buildDocumentFilterOptions(billing.data, getLpLikeDocument, ["number"], [
        {
          value: "status:provider_invoice_pending",
          label: "Factura proveedor pendiente",
          group: "Proveedor",
        },
        {
          value: "status:provider_invoice_received",
          label: "Factura proveedor recibida",
          group: "Proveedor",
        },
      ], LP_DOCUMENT_STATUS_OPTIONS),
    [billing.data]
  );
  const commissionFilterOptions = useMemo(
    () =>
      buildDocumentFilterOptions(
        billing.data,
        (row) => row.invoice_tomador_comision,
        ["number", "invoice_number"]
      ),
    [billing.data]
  );

  const filteredBillingData = useMemo(() => {
    const filteredRows = (billing.data || [])
      .filter((row) => {
        return (
          documentMatchesFilter(
            row.invoice_id,
            documentFilters.invoice,
            ["invoice_number", "number"]
          ) &&
          documentMatchesFilter(getLpLikeDocument(row), documentFilters.lp, [
            "number",
          ], {
            row,
            column: "lp",
          }) &&
          documentMatchesFilter(
            row.invoice_tomador_comision,
            documentFilters.commission,
            ["number", "invoice_number"]
          )
        );
      })
      .sort((a, b) => {
        const invoiceA =
          a.invoice_id?.invoice_number || a.invoice_id?._id || "zz";
        const invoiceB =
          b.invoice_id?.invoice_number || b.invoice_id?._id || "zz";
        const invoiceCompare = invoiceA.localeCompare(invoiceB);
        if (invoiceCompare !== 0) return invoiceCompare;

        const tomadorA = a.id_tomador?.reason || a.id_tomador?.cuit || "";
        const tomadorB = b.id_tomador?.reason || b.id_tomador?.cuit || "";
        const tomadorCompare = tomadorA.localeCompare(tomadorB);
        if (tomadorCompare !== 0) return tomadorCompare;

        const tripA = `${a.parent_id || ""}${a.order || ""}`;
        const tripB = `${b.parent_id || ""}${b.order || ""}`;
        return tripA.localeCompare(tripB);
      });

    return applyVisibleRowSpan(filteredRows);
  }, [billing.data, documentFilters]);

  const CheckCell = ({ rowData, onChange, checkedKeys, dataKey, ...props }) => (
    <Cell {...props} style={{ padding: 0 }}>
      <div style={{ lineHeight: "20px" }}>
        <Checkbox
          style={{ marginLeft: "auto", marginRight: "auto" }}
          value={rowData[dataKey]}
          inline
          onChange={onChange}
          checked={checkedKeys.some((item) => item === rowData[dataKey])}
          disabled={
            !checkeableRows.data.some((item) => item._id === rowData._id)
          }
        />
      </div>
    </Cell>
  );

  const handleCheckAll = (value, checked) => {
    const keys = checked
      ? checkeableRows.data.map((item) => {
          if (typeof item.auditedWeight === "number") return item._id;
        })
      : [];
    setCheckedKeys(keys);
  };
  const handleCheck = (value, checked) => {
    const keys = checked
      ? [...checkedKeys, value]
      : checkedKeys.filter((item) => item !== value);
    setCheckedKeys(keys);
  };
  const NameCell = ({ rowData, dataKey, ...props }) => {
    const speaker = (
      <Popover>
        <Container>
          <h6 className="h6 text-primary font-weight-bold">Dador</h6>

          <p className="m-0">
            <b>Cuit:</b> {rowData.id_dador?.cuit}
          </p>
          <p className="m-0">
            <b>Mail:</b> {rowData.id_dador?.email}
          </p>
        </Container>

        <Container className="border-top mt-2 pt-2">
          <h6 className="h6 text-primary font-weight-bold">Tomador</h6>
          <p className="m-0">
            <b>Cuit:</b> {rowData.id_tomador?.cuit}
          </p>
          <p className="m-0">
            <b>Mail:</b> {rowData.id_tomador?.email}
          </p>
        </Container>
      </Popover>
    );

    return (
      <Cell {...props}>
        <Whisper placement="top" speaker={speaker}>
          <a className="mb-0">
            {rowData.parent_id + rowData.order || "No posee"}{" "}
          </a>
        </Whisper>
      </Cell>
    );
  };

  const handleInvoice = () => {
    if (checkedKeys.length > 0) {
      const toInvoice = checkeableRows.data.filter((row) =>
        checkedKeys.includes(row._id)
      );
      setInvoiceModal((prev) => {
        return { data: toInvoice, isShow: true };
      });
    }
  };

  const calculateDadorBalance = () => {
    const uniqueInvoiceIds = new Set();
    let sum = 0;

    billing.data?.forEach((item) => {
      const invoiceId = item.invoice_id?._id;

      if (typeof invoiceId === "string" && !uniqueInvoiceIds.has(invoiceId)) {
        uniqueInvoiceIds.add(invoiceId);

        const amount = item.invoice_id?.amount || 0;
        const creditNoteAmount = getActiveCreditNoteAmount(item.invoice_id);
        const receiptAmounts =
          item.invoice_id?.receipts?.map((receipt) => receipt.receipt_amount) ||
          [];

        const totalReceiptAmount = receiptAmounts.reduce(
          (accumulator, currentItem) => accumulator + currentItem,
          0
        );

        sum += amount - creditNoteAmount - totalReceiptAmount;
      }
    });

    setDadorBalance(sum);
  };
  const calculateTomadorBalance = () => {
    const uniqueLpIds = new Set();
    let sum = 0;

    billing.data?.forEach((item) => {
      const lpId = item.lp_id?._id;

      if (typeof lpId === "string" && !uniqueLpIds.has(lpId)) {
        uniqueLpIds.add(lpId);

        const amount = item.lp_id?.amount || 0;
        const commissionAmount = item.invoice_tomador_comision?.amount || 0;
        const paymentOrderAmounts =
          item.lp_id?.paymentOrder_id?.map(
            (paymentOrder) => paymentOrder.amount
          ) || [];

        const totalPaymentOrderAmount = paymentOrderAmounts.reduce(
          (accumulator, currentItem) => accumulator + currentItem,
          0
        );

        sum += amount - commissionAmount - totalPaymentOrderAmount;
      }
    });

    setTomadorBalance(sum);
  };

  const calculateInvoiceDador = () => {
    const uniqueInvoiceIds = new Set();
    let sum = 0;

    billing.data?.forEach((item) => {
      const invoiceId = item.invoice_id?._id;

      if (typeof invoiceId === "string" && !uniqueInvoiceIds.has(invoiceId)) {
        uniqueInvoiceIds.add(invoiceId);

        const amount = item.invoice_id?.amount || 0;

        sum += amount;
      }
    });

    setDadorInvoice(sum);
  };
  const calculateLpTomador = () => {
    const uniqueLpIds = new Set();
    let sum = 0;

    billing.data?.forEach((item) => {
      const lpId = item.lp_id?._id;

      if (typeof lpId === "string" && !uniqueLpIds.has(lpId)) {
        uniqueLpIds.add(lpId);

        const amount = item.lp_id?.amount || 0;

        sum += amount;
      }
    });

    setTomadorLp(sum);
  };
  const getData = async () => {
    try {
      const { total, accounts, id_dador, id_tomador } = await getBillingsAdmin(
        query
      );
      setBilling({ total, data: accounts, id_dador, id_tomador });
      if (receiptModal.data) {
        let updatedData = accounts.find(
          (ele) => ele._id === receiptModal.data._id
        );
        setReceiptModal((prev) => {
          return {
            ...prev,
            data: updatedData
              ? buildReceiptModalData(updatedData, accounts)
              : prev.data,
          };
        });
      }
      if (lpModal.data) {
        let updatedData = accounts.find((ele) => ele._id === lpModal.data._id);
        setLpModal((prev) => {
          return { ...prev, data: updatedData };
        });
      }
    } catch (error) {
      console.error("Billing Admin", error);
    }
  };
  const onRefetch = () => setRefetch((prev) => !prev);
  const getUsers = async () => {
    try {
      const { tomador, dador } = await getUserPending();
      setPendingDador(dador);
      setPendingTomador(tomador);
    } catch (error) {
      console.error("Get pending to audited", error);
    }
  };

  useEffect(() => {
    getUsers();
  }, []);

  useEffect(() => {
    const handleBillingDocumentUpdated = () => {
      setRefetch((prev) => !prev);
    };

    window.addEventListener(
      "billing-document-updated",
      handleBillingDocumentUpdated
    );

    return () => {
      window.removeEventListener(
        "billing-document-updated",
        handleBillingDocumentUpdated
      );
    };
  }, []);

  useEffect(() => {
    getData();
    setCheckedKeys([]);
  }, [refetch, query]);

  useEffect(() => {
    calculateDadorBalance();
    calculateInvoiceDador();
    calculateTomadorBalance();
    calculateLpTomador();
    const visibleRows = filteredBillingData || [];
    const areDadorsEqual = visibleRows.every(
      (row) => row.id_dador._id === visibleRows[0].id_dador._id
    );
    if (!visibleRows.length || !areDadorsEqual) {
      setCheckeableRows(INITIAL_STATE);
      return;
    }

    const filteredRows = visibleRows.filter((row) => {
      return (
        typeof row.auditedWeight === "number" &&
        !row.invoice_id &&
        row.id_dador._id === visibleRows[0].id_dador._id
      );
    });
    setCheckeableRows({
      data: filteredRows,
      total: filteredRows.length,
    });
  }, [billing, filteredBillingData]);

  useEffect(() => {
    setCheckedKeys((prevKeys) =>
      prevKeys.filter((key) =>
        checkeableRows.data.some((row) => row._id === key)
      )
    );
  }, [checkeableRows]);

  if (checkeableRows?.total > 0 && checkedKeys.length === checkeableRows.total) {
    checked = true;
  } else if (checkedKeys.length === 0) {
    checked = false;
  } else if (
    checkedKeys.length > 0 &&
    checkedKeys.length < checkeableRows?.total
  ) {
    indeterminate = true;
  }
  return (
    <Container fluid>
      <SearchToInvoice
        dador={pendingDador}
        tomador={pendingTomador}
        query={query}
        setQuery={setQuery}
        selectedDador={selectedDador}
        setSelectedDador={setSelectedDador}
        onRefresh={onRefetch}
        onClearFilters={resetDocumentFilters}
        showClearFilters={hasActiveDocumentFilters}
      />

      <Row className="mt-3">
        <Col md="12" className="d-flex justify-content-end">
          <Dropdown alignRight>
            <Dropdown.Toggle variant="primary" className="font-weight-bold">
              Reportes
            </Dropdown.Toggle>
            <Dropdown.Menu>
              <Dropdown.Item onClick={() => setSalesVatBookModal(true)}>
                IVA ventas
              </Dropdown.Item>
              <Dropdown.Item onClick={() => setPurchaseVatBookModal(true)}>
                IVA compras
              </Dropdown.Item>
            </Dropdown.Menu>
          </Dropdown>
        </Col>
      </Row>

      <Row className="mt-3">
        {/* <Col md="12">
          <h1 className=" h6 font-weight-bold font-italic">
            {TAXES_LABEL || "Montos netos sin impuestos"}
          </h1>
        </Col> */}
        <Col md="12">
          <Table
            rowHeight={30}
            hover={false}
            height={550}
            bordered
            cellBordered
            headerHeight={70}
            wordWrap={true}
            data={filteredBillingData}
            loading={isLoading}
            rowClassName={getBillingRowClassName}
            onRowClick={(rowData, event) => {
              const clickedElementClass = event.target.className;
              if (
                typeof clickedElementClass !== "string" ||
                clickedElementClass.includes("rs-checkbox-wrapper") ||
                clickedElementClass instanceof SVGAnimatedString
              ) {
                return;
              }

              setSelectedDador(rowData?.id_dador?._id);
            }}
          >
            <ColumnGroup header="Viajes Finalizados" align="center">
              <Column flexGrow={1}>
                <HeaderCell>
                  <div
                    style={{
                      position: "absolute",
                      top: 0,
                      left: 0,
                      bottom: 0,
                      right: 0,
                    }}
                  >
                    <Checkbox
                      style={{ marginLeft: "auto", marginRight: "auto" }}
                      inline
                      checked={checked}
                      indeterminate={indeterminate}
                      onChange={handleCheckAll}
                      disabled={checkeableRows.total === 0}
                    />
                  </div>
                </HeaderCell>

                <CheckCell
                  dataKey="_id"
                  checkedKeys={checkedKeys}
                  onChange={handleCheck}
                />
              </Column>
              <Column flexGrow={2}>
                <HeaderCell>ID viaje</HeaderCell>
                <NameCell dataKey="ID_VIAJE" />
              </Column>
              <Column flexGrow={3}>
                <HeaderCell>Importe + IVA</HeaderCell>
                <Cell dataKey="billing_amount">
                  {(rowData) => {
                    return typeof rowData.auditedWeight === "number" ? (
                      <CurrencyFormat
                        value={rowData.price_per_ton * rowData.auditedWeight}
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix={"$"}
                        renderText={(formattedValue) => (
                          <div>
                            <span>{formattedValue}</span>
                            {rowData.invoice_id ? null : (
                              <BiEdit
                                className="cursor-pointer ml-1"
                                onClick={() => openAuditedModal(rowData)}
                              />
                            )}
                          </div>
                        )}
                      />
                    ) : (
                      <div>
                        <span>Auditar Peso </span>
                        <BiEdit
                          className="cursor-pointer ml-1 mb-1"
                          onClick={() => openAuditedModal(rowData)}
                        />
                      </div>
                    );
                  }}
                </Cell>
              </Column>
            </ColumnGroup>
            <ColumnGroup header="Dador" align="center">
              <Column
                flexGrow={3}
                verticalAlign="top"
                rowSpan={(rowData) => rowData.row_span}
              >
                <HeaderCell>
                  <DocumentFilterHeader
                    title="Facturas"
                    data={invoiceFilterOptions}
                    value={documentFilters.invoice}
                    onChange={(value) =>
                      updateDocumentFilter("invoice", value || [])
                    }
                  />
                </HeaderCell>
                <Cell dataKey="billing_number">
                  {(rowData) => {
                    const hasError =
                      rowData.invoice_id?.status === "error" ||
                      rowData.invoice_dador_comision?.status === "error" ||
                      rowData.invoice_id?.status === "unauthorized" ||
                      rowData.invoice_dador_comision?.status === "unauthorized";

                    if (hasError) {
                      return (
                        <BiErrorCircle
                          size={20}
                          className="text-danger cursor-pointer"
                          onClick={() => {
                            if (
                              rowData.invoice_id?.status === "error" ||
                              rowData.invoice_id?.status === "unauthorized"
                            ) {
                              openErrorModal({
                                type: "invoice",
                                data: rowData.invoice_id,
                              });
                              return;
                            }

                            if (
                              rowData.invoice_dador_comision?.status ===
                                "error" ||
                              rowData.invoice_dador_comision?.status ===
                                "unauthorized"
                            ) {
                              openErrorModal({
                                type: "invoiceDadorComision",
                                data: rowData.invoice_dador_comision,
                              });
                              return;
                            }
                          }}
                        />
                      );
                    }

                    if (rowData.invoice_id?.invoice_number) {
                      return (
                        <DocumentAction
                          number={rowData.invoice_id.invoice_number}
                          onClick={() => openReceiptModal(rowData)}
                        />
                      );
                    }

                    if (rowData.invoice_id?._id) return (
                      <PendingInvoiceRetry invoice={rowData.invoice_id} refetch={onRefetch} />
                    );

                    return <p>No posee</p>;
                  }}
                </Cell>
              </Column>
              <Column
                flexGrow={3}
                align="center"
                rowSpan={(rowData) => rowData.row_span}
              >
                <HeaderCell>Saldo Factura</HeaderCell>
                <Cell dataKey="invoice_balance">
                  {(rowData) => {
                    return rowData.invoice_id?.invoice_number ? (
                      <CurrencyFormat
                        value={
                          (rowData.invoice_id?.amount || 0) -
                          getActiveCreditNoteAmount(rowData.invoice_id) -
                          (rowData.invoice_id?.receipts?.reduce(
                            (accumulator, currentItem) => {
                              return accumulator + currentItem.receipt_amount;
                            },
                            0
                          ) || 0)
                        }
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix={"$"}
                      />
                    ) : (
                      <p className="mb-0">No posee</p>
                    );
                  }}
                </Cell>
              </Column>
              <Column flexGrow={2}>
                <HeaderCell>Comision</HeaderCell>
                <Cell dataKey="comision_dador">
                  {(rowData) => {
                    const importeDador =
                      typeof rowData.auditedWeight === "number"
                        ? rowData.price_per_ton *
                          rowData.auditedWeight *
                          (rowData.discount_dador / 100)
                        : null;

                    return (
                      <>
                        <p className="mb-0">
                          {rowData.discount_dador
                            ? `${rowData.discount_dador}%`
                            : "No posee"}
                        </p>
                        {importeDador ? (
                          <CurrencyFormat
                            value={importeDador}
                            displayType="text"
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            renderText={(formatted) => (
                              <small className="text-muted">
                                ({formatted})
                              </small>
                            )}
                          />
                        ) : null}
                      </>
                    );
                  }}
                </Cell>
              </Column>
            </ColumnGroup>

            <ColumnGroup header="Tomador" align="center">
              <Column flexGrow={3} rowSpan={(rowData) => rowData.lp_row_span}>
                <HeaderCell>
                  <DocumentFilterHeader
                    title="LP/Proforma + IVA"
                    data={lpFilterOptions}
                    value={documentFilters.lp}
                    onChange={(value) =>
                      updateDocumentFilter("lp", value || [])
                    }
                  />
                </HeaderCell>
                <Cell dataKey="lp_number">
                  {(rowData) => {
                    if (isCompraventaInvoice(rowData)) {
                      if (rowData.provider_proforma_id?.status === "error") {
                        return (
                          <span className="text-danger">
                            Error proforma
                          </span>
                        );
                      }

                      if (rowData.provider_proforma_id?.number) {
                        return (
                          <div className="d-flex flex-column align-items-center">
                            <div className="d-flex align-items-center">
                              <BiEdit
                                className="cursor-pointer mr-1"
                                title="Editar proforma"
                                onClick={() =>
                                  openProviderProformaEditModal(rowData)
                                }
                              />
                              <small
                                className="text-primary number-font text-center cursor-pointer"
                                onClick={() => {
                                  if (rowData.provider_proforma_id?.pdf_url) {
                                    window.open(
                                      rowData.provider_proforma_id.pdf_url,
                                      "_blank"
                                    );
                                  }
                                }}
                              >
                                {rowData.provider_proforma_id.number}
                              </small>
                            </div>
                            {rowData.provider_proforma_id
                              ?.provider_invoice_pdf_url ? (
                              <div className="d-flex align-items-center mt-1">
                                <small
                                  className="text-primary cursor-pointer"
                                  onClick={() =>
                                    window.open(
                                      rowData.provider_proforma_id
                                        .provider_invoice_pdf_url,
                                      "_blank"
                                    )
                                  }
                                >
                                  {rowData.provider_proforma_id
                                    .provider_invoice_number ||
                                    "Factura proveedor"}
                                </small>
                                <BiEdit
                                  className="cursor-pointer ml-1"
                                  title="Editar factura proveedor"
                                  onClick={() =>
                                    openProviderInvoiceModal(
                                      rowData.provider_proforma_id
                                    )
                                  }
                                />
                              </div>
                            ) : (
                              <small
                                className="text-primary cursor-pointer mt-1"
                                onClick={() =>
                                  openProviderInvoiceModal(
                                    rowData.provider_proforma_id
                                  )
                                }
                              >
                                Cargar factura proveedor
                              </small>
                            )}
                          </div>
                        );
                      }

                      if (rowData.provider_proforma_id?._id) {
                        return <span>Pendiente...</span>;
                      }

                      if (rowData.invoice_id?.status === "authorized") {
                        return (
                          <p
                            className="cursor-pointer text-primary"
                            onClick={() =>
                              openTryAgain(rowData, "Generar proforma")
                            }
                          >
                            Generar proforma
                          </p>
                        );
                      }

                      if (rowData.invoice_id?._id) {
                        return <span>Pendiente...</span>;
                      }

                      return <p>No posee</p>;
                    }

                    const hasError =
                      rowData.lp_id?.status === "error" ||
                      rowData.lp_id?.status === "unauthorized";

                    if (hasError) {
                      return (
                        <BiErrorCircle
                          size={20}
                          className="text-danger cursor-pointer"
                          onClick={() => {
                            if (
                              rowData.lp_id?.status === "error" ||
                              rowData.lp_id?.status === "unauthorized"
                            ) {
                              openErrorModal({
                                type: "liquidProduct",
                                data: rowData.lp_id,
                              });
                            }
                          }}
                        />
                      );
                    }

                    if (rowData.lp_id?.number) {
                      return (
                        <DocumentAction
                          number={rowData.lp_id.number}
                          onClick={() => openLpModal(rowData)}
                        />
                      );
                    }

                    if (rowData.lp_id?._id) return <span>Pendiente...</span>;

                    if (
                      rowData.invoice_id?.status === "authorized"
                    ) {
                      return (
                        <p
                          className="cursor-pointer text-primary"
                          onClick={() => openTryAgain(rowData, "Generar LP")}
                        >
                          Generar LP
                        </p>
                      );
                    }

                    if (rowData.invoice_id?._id) return <span>Pendiente...</span>;

                    return <p>No posee</p>;
                  }}
                </Cell>
              </Column>
              <Column flexGrow={3} rowSpan={(rowData) => rowData.lp_row_span}>
                <HeaderCell>Saldo LP</HeaderCell>
                <Cell dataKey="tomador_balance">
                  {(rowData) => {
                    if (isCompraventaInvoice(rowData)) {
                      if (rowData.provider_proforma_id?.amount) {
                        return (
                          <span
                            className="cursor-pointer"
                            onClick={() => openLpModal(rowData)}
                            title="Ver estado de cuenta"
                          >
                            <CurrencyFormat
                              value={
                                rowData.provider_proforma_id.amount -
                                getPaymentOrderTotal(
                                  rowData.provider_proforma_id.paymentOrder_id
                                )
                              }
                              displayType={"text"}
                              thousandSeparator={true}
                              decimalScale={2}
                              fixedDecimalScale={true}
                              prefix={"$"}
                            />
                          </span>
                        );
                      }

                      if (rowData.invoice_id?.status === "authorized") {
                        return <p className="mb-0">Pendiente de proforma</p>;
                      }

                      return <p className="mb-0">No posee</p>;
                    }

                    if (
                      rowData.invoice_id?.status === "authorized" &&
                      rowData.lp_id?.number
                    ) {
                      return (
                        <CurrencyFormat
                          value={
                            rowData.lp_id?.amount -
                            (rowData.invoice_tomador_comision?.amount || 0) -
                            getPaymentOrderTotal(
                              rowData.lp_id?.paymentOrder_id
                            )
                          }
                          displayType={"text"}
                          thousandSeparator={true}
                          decimalScale={2}
                          fixedDecimalScale={true}
                          prefix={"$"}
                        />
                      );
                    }
                    if (
                      rowData.invoice_id?.status === "authorized" &&
                      !rowData.lp_id?._id
                    ) {
                      return <p className="mb-0">Pendiente de LP</p>;
                    }

                    return <p className="mb-0">No posee</p>;
                  }}
                </Cell>
              </Column>
              <Column
                flexGrow={2}
                rowSpan={(rowData) => rowData.commission_row_span}
              >
                <HeaderCell>
                  <DocumentFilterHeader
                    title="Comision"
                    data={commissionFilterOptions}
                    value={documentFilters.commission}
                    onChange={(value) =>
                      updateDocumentFilter("commission", value || [])
                    }
                  />
                </HeaderCell>
                <Cell dataKey="comision_tomador">
                  {(rowData) => {
                    const hasError =
                      rowData.invoice_tomador_comision?.status === "error" ||
                      rowData.invoice_tomador_comision?.status ===
                        "unauthorized";
                    const importeTomador = getTomadorCommissionAmount(rowData);

                    if (isCompraventaInvoice(rowData)) {
                      return <p className="mb-0">No aplica</p>;
                    }

                    if (hasError) {
                      return (
                        <BiErrorCircle
                          size={20}
                          className="text-danger cursor-pointer"
                          onClick={() =>
                            openErrorModal({
                              type: "invoiceTomadorComision",
                              data: rowData.invoice_tomador_comision,
                            })
                          }
                        />
                      );
                    }

                    if (rowData.invoice_tomador_comision?.number) {
                      return (
                        <div className="d-flex flex-column align-items-center">
                          <CurrencyFormat
                            value={
                              rowData.invoice_tomador_comision?.amount || 0
                            }
                            displayType="text"
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                          />
                          <small className="text-primary number-font text-center">
                            {rowData.invoice_tomador_comision.number}
                          </small>
                        </div>
                      );
                    }

                    if (rowData.invoice_tomador_comision?._id) {
                      return <span>Pendiente...</span>;
                    }

                    if (
                      rowData.lp_id?.status === "authorized" &&
                      hasTomadorCommission(rowData)
                    ) {
                      return (
                        <small
                          className="cursor-pointer text-primary"
                          onClick={() =>
                            openTryAgain(rowData, "Generar comisión tomador")
                          }
                        >
                          Generar comprobante
                        </small>
                      );
                    }

                    return (
                      <>
                        <p className="mb-0">
                          {typeof rowData.discount_trip === "number"
                            ? `${rowData.discount_trip}% viaje`
                            : rowData.discount_tomador
                            ? `${rowData.discount_tomador}%`
                            : "No posee"}
                        </p>
                        {importeTomador ? (
                          <CurrencyFormat
                            value={importeTomador}
                            displayType="text"
                            thousandSeparator
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$"
                            renderText={(formatted) => (
                              <small className="text-muted">
                                ({formatted})
                              </small>
                            )}
                          />
                        ) : null}
                      </>
                    );
                  }}
                </Cell>
              </Column>
            </ColumnGroup>
            <ColumnGroup header="Comision especial viaje" align="center">
              <Column flexGrow={2}>
                <HeaderCell>% Viaje</HeaderCell>
                <Cell dataKey="comision_trip">
                  {(rowData, rowIndex) => {
                    return (
                      <>
                        <p className="mb-0">
                          {typeof rowData.discount_trip === "number" &&
                          rowData.discount_trip >= 0
                            ? `${rowData.discount_trip}%`
                            : "N/D"}{" "}
                        </p>
                      </>
                    );
                  }}
                </Cell>
              </Column>
              <Column flexGrow={3}>
                <HeaderCell>Importe</HeaderCell>
                <Cell dataKey="discount_amount">
                  {(rowData) => {
                    const hasTripCommission =
                      typeof rowData.discount_trip === "number" &&
                      rowData.discount_trip >= 0 &&
                      typeof rowData.auditedWeight === "number";

                    return hasTripCommission ? (
                      <CurrencyFormat
                        value={
                          rowData.price_per_ton *
                          rowData.auditedWeight *
                          (rowData.discount_trip / 100)
                        }
                        displayType={"text"}
                        thousandSeparator={true}
                        decimalScale={2}
                        fixedDecimalScale={true}
                        prefix={"$"}
                        renderText={(formattedValue) => (
                          <span>{formattedValue}</span>
                        )}
                      />
                    ) : (
                      <span>N/D</span>
                    );
                  }}
                </Cell>
              </Column>
            </ColumnGroup>
          </Table>
        </Col>
      </Row>
      <Row>
        <Col className="d-flex justify-content-center mt-2">
          <LogButton
            className="w-50"
            color="primary"
            onClick={handleInvoice}
            title={"Facturar Seleccionados"}
            border="curve"
            disabled={checkedKeys.length < 1}
          />
        </Col>
      </Row>
      <Row>
        <Col md={6}>
          <CardLogBootstrapStyled className="shadow-6dp p-4 ml-0 mr-0 mt-3 mb-4">
            <Row className="ml-0  mb-2">
              <h5 className="h5 text-primary font-weight-bold ">
                {billing.id_dador && billing.data[0]?.id_dador?.email
                  ? billing.data[0]?.id_dador?.email
                  : "Dadores"}{" "}
              </h5>
            </Row>
            <Row>
              <Col xs={6}>
                {" "}
                <label className="black-primary font-weight-bold mr-2">
                  Importe total viajes:
                </label>
              </Col>
              <Col xs={6}>
                <>
                  <CurrencyFormat
                    value={
                      billing.data
                        ?.filter(
                          (item) => typeof item.auditedWeight === "number"
                        )
                        ?.reduce((sum, item) => {
                          return sum + item.price_per_ton * item.auditedWeight;
                        }, 0) * 1.21
                    }
                    displayType={"text"}
                    thousandSeparator={true}
                    decimalScale={2}
                    fixedDecimalScale={true}
                    prefix={"$"}
                    renderText={(formattedValue) => (
                      <div>
                        <span>{formattedValue}</span>
                      </div>
                    )}
                  />
                </>
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                {" "}
                <label className="black-primary font-weight-bold mr-2">
                  Importe total facturado:
                </label>
              </Col>
              <Col xs={6}>
                <CurrencyFormat
                  value={dadorInvoice}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix={"$"}
                  renderText={(formattedValue) => (
                    <div>
                      <span>{formattedValue}</span>
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                {" "}
                <label className="black-primary font-weight-bold mr-2">
                  Saldo de facturas:
                </label>
              </Col>
              <Col xs={6}>
                <CurrencyFormat
                  value={dadorBalance}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix={"$"}
                  renderText={(formattedValue) => (
                    <div>
                      <span>{formattedValue}</span>
                    </div>
                  )}
                />
              </Col>
            </Row>
          </CardLogBootstrapStyled>
        </Col>
        <Col md={6}>
          <CardLogBootstrapStyled className="shadow-6dp p-4 ml-0 mr-0 mt-3 mb-4">
            <Row className="ml-0  mb-2">
              <h5 className="h5 text-primary font-weight-bold">
                {billing.id_tomador && billing.data[0]?.id_tomador?.email
                  ? billing.data[0]?.id_tomador?.email
                  : "Tomadores"}
              </h5>
            </Row>
            <Row>
              <Col xs={6}>
                {" "}
                <label className="black-primary font-weight-bold mr-2">
                  Importe total viajes:
                </label>
              </Col>
              <Col xs={6}>
                <CurrencyFormat
                  value={
                    (billing.data
                      ?.filter((item) => typeof item.auditedWeight === "number")
                      ?.reduce((sum, item) => {
                        return sum + item.price_per_ton * item.auditedWeight;
                      }, 0) -
                      billing.data
                        ?.filter(
                          (item) => typeof item.auditedWeight === "number"
                        )
                        ?.reduce((sum, item) => {
                          return (
                            sum +
                            (item.discount_trip != null
                              ? item.price_per_ton *
                                item.auditedWeight *
                                (item.discount_trip / 100)
                              : item.price_per_ton *
                                item.auditedWeight *
                                ((item.discount_dador + item.discount_tomador) /
                                  100))
                          );
                        }, 0)) *
                    1.21
                  }
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix={"$"}
                  renderText={(formattedValue) => (
                    <div>
                      <span>{formattedValue}</span>
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                {" "}
                <label className="black-primary font-weight-bold mr-2">
                  Importe total LP:
                </label>
              </Col>
              <Col xs={6}>
                <CurrencyFormat
                  value={tomadorLp}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix={"$"}
                  renderText={(formattedValue) => (
                    <div>
                      <span>{formattedValue}</span>
                    </div>
                  )}
                />
              </Col>
            </Row>
            <Row>
              <Col xs={6}>
                {" "}
                <label className="black-primary font-weight-bold mr-2">
                  Saldo de LP:
                </label>
              </Col>
              <Col xs={6}>
                <CurrencyFormat
                  value={tomadorBalance}
                  displayType={"text"}
                  thousandSeparator={true}
                  decimalScale={2}
                  fixedDecimalScale={true}
                  prefix={"$"}
                  renderText={(formattedValue) => (
                    <div>
                      <span>{formattedValue}</span>
                    </div>
                  )}
                />
              </Col>
            </Row>
          </CardLogBootstrapStyled>
        </Col>
      </Row>

      {showAudited?.isShow ? (
        <ModalAuditedWeight
          isShow={showAudited.isShow}
          data={showAudited.data}
          handleClose={closeAuditedModal}
          refetch={onRefetch}
        />
      ) : null}
      {errorModal.isShow && (
        <ModalError
          isShow={errorModal.isShow}
          type={errorModal.type}
          data={errorModal.data}
          handleClose={closeErrorModal}
          refetch={onRefetch}
        />
      )}

      {invoiceModal?.isShow ? (
        <ModalInvoice
          isShow={invoiceModal.isShow}
          data={invoiceModal.data}
          tryAgain={invoiceModal.tryAgain}
          actionLabel={invoiceModal.actionLabel}
          providerProforma={invoiceModal.providerProforma}
          handleClose={() => {
            setInvoiceModal((prev) => {
              return { ...prev, isShow: false };
            });
          }}
          refetch={onRefetch}
        />
      ) : null}
      {receiptModal?.isShow ? (
        <ModalReceipt
          isShow={receiptModal.isShow}
          data={receiptModal.data}
          handleClose={closeReceiptModal}
          refetch={onRefetch}
        />
      ) : null}
      {lpModal?.isShow ? (
        <ModalLp
          isShow={lpModal.isShow}
          data={lpModal.data}
          amount={lpModal.amount}
          handleClose={closeLpModal}
          refetch={onRefetch}
        />
      ) : null}
      <ModalLogStyled
        show={providerInvoiceModal.isShow}
        onHide={closeProviderInvoiceModal}
        centered
      >
        <Modal.Header>
          <CloseButton type="button" onClick={closeProviderInvoiceModal}>
            <FiX />
          </CloseButton>
        </Modal.Header>
        <Modal.Body>
          <Row>
            <Col xs={12}>
              <h4 className="text-primary font-weight-bold mb-2">
                Factura proveedor
              </h4>
              <hr />
            </Col>
          </Row>
          <Row>
            <Col md={12}>
              <Form.Group>
                <Form.Label className="font-weight-bold">
                  Número de factura
                </Form.Label>
                <LogInput
                  errors={{}}
                  name="provider_invoice_number"
                  type="text"
                  value={providerInvoiceModal.form.provider_invoice_number}
                  onChange={(event) =>
                    updateProviderInvoiceForm(
                      "provider_invoice_number",
                      event.target.value
                    )
                  }
                  placeholder="FA 00001-00000001"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="font-weight-bold">Fecha</Form.Label>
                <LogInput
                  errors={{}}
                  name="provider_invoice_date"
                  type="date"
                  value={providerInvoiceModal.form.provider_invoice_date}
                  onChange={(event) =>
                    updateProviderInvoiceForm(
                      "provider_invoice_date",
                      event.target.value
                    )
                  }
                />
              </Form.Group>
            </Col>
            <Col md={6}>
              <Form.Group>
                <Form.Label className="font-weight-bold">Importe</Form.Label>
                <LogInput
                  errors={{}}
                  name="provider_invoice_amount"
                  type="number"
                  min="0"
                  step="0.01"
                  value={providerInvoiceModal.form.provider_invoice_amount}
                  onChange={(event) =>
                    updateProviderInvoiceForm(
                      "provider_invoice_amount",
                      event.target.value
                    )
                  }
                  placeholder="0.00"
                />
              </Form.Group>
            </Col>
          </Row>
          <Row>
            <Col xs={12}>
              <Form.Group>
                <Form.Label className="font-weight-bold">Archivo</Form.Label>
                <UploadBox>
                  <FiUpload size={22} />
                  <span>
                    {providerInvoiceModal.form.provider_invoice_file?.name ||
                      "Seleccionar factura del proveedor"}
                  </span>
                  <small>PDF, JPG o PNG</small>
                  <input
                    type="file"
                    accept=".pdf,image/*"
                    onChange={(event) =>
                      updateProviderInvoiceForm(
                        "provider_invoice_file",
                        event.target.files?.[0] || null
                      )
                    }
                  />
                </UploadBox>
                {providerInvoiceModal.proforma?.provider_invoice_pdf_url ? (
                  <ExistingProviderFile
                    onClick={() =>
                      window.open(
                        providerInvoiceModal.proforma.provider_invoice_pdf_url,
                        "_blank"
                      )
                    }
                  >
                    <FiFileText />
                    Ver archivo cargado
                  </ExistingProviderFile>
                ) : null}
              </Form.Group>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="justify-content-center">
          <LogButton
            title="Guardar factura"
            type="button"
            onClick={submitProviderInvoice}
          />
        </Modal.Footer>
      </ModalLogStyled>
      {salesVatBookModal ? (
        <ModalSalesVatBook
          isShow={salesVatBookModal}
          handleClose={() => setSalesVatBookModal(false)}
        />
      ) : null}
      {purchaseVatBookModal ? (
        <ModalPurchaseVatBook
          isShow={purchaseVatBookModal}
          handleClose={() => setPurchaseVatBookModal(false)}
        />
      ) : null}
    </Container>
  );
}

export default BillingAdmin;
